import React, { useCallback, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Table, Badge } from "react-bootstrap";
import FilterBar from "components/common/FilterBar";
import { useTranslation } from "react-i18next";
import {
  getRoles,
  resetFilters,
  setFilter,
  updateRoleStatus,
} from "store/slices/rolesSlice";
import { formatPermissions } from "./roleHelper";
import { formatUTCDate, getMinMaxDate } from "utils/utils";
import { getActionFlags } from "utils/getActionFlags";
import { Link, useNavigate } from "react-router-dom";
import { Icons } from "assets/icons";
import { showDeleteModal } from "store/slices/modalSlice";
import Switch from "components/common/Switch";
import { usePermission } from "hooks/usePermission";
import TableShimmer from "components/common/TableShimmer";
import NoDataFound from "components/common/NoDataFound";
import {
  ADMIN_ACCOUNT_TYPE,
  ORDER_BY_OPTIONS,
  STATUS_OPTIONS,
} from "utils/constants";
import { handleStatusUpdate } from "helpers/apiUtils";
import TruncatedText from "components/common/TruncatedText";
import Paginations from "components/common/Paginations";
import DataTable from "components/common/table/DataTable";

const Roles = () => {
  const { t } = useTranslation();
  const { trash, edit, noRoles, view } = Icons || {};
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { canAction } = usePermission();
  const { canView, canEdit, canDelete, canAdd } = canAction("roles");

  const {
    loading,
    isLoading,
    loadingItemId,
    items: roles,
    totalPages,
    currentPage,
    totalCount,
    filters: { keyword, dates, status, orderBy, roleType },
  } = useSelector((state) => state.Roles);

  useEffect(() => {
    if (canView) {
      getAllData();
    }
  }, []);

  const getAllData = (page, action) => {
    const { isReset, isResetKeyword } = getActionFlags(action);

    const payload = {
      perPage: 50,
      pageNo: page || 1,
      orderBy: isReset ? -1 : orderBy,
      status: isReset ? -1 : status,
      roleType: isReset ? "" : roleType,

      keyword: isResetKeyword ? "" : keyword || "",
      dateRange: isReset ? {} : getMinMaxDate(dates),
    };
    dispatch(getRoles(payload));
  };

  const handleReset = () => {
    dispatch(resetFilters());
    getAllData(1, { actionType: "RESET" });
  };

  const filtersList = [
    {
      key: "status",
      placeholder: t("Status"),
      type: "singleSelect",
      options: STATUS_OPTIONS,
    },
    {
      key: "orderBy",
      placeholder: t("Order By"),
      type: "singleSelect",
      options: ORDER_BY_OPTIONS,
    },
    {
      key: "roleType",
      placeholder: t("Role Type"),
      type: "singleSelect",
      options: ADMIN_ACCOUNT_TYPE.filter((roleType) =>{return roleType.name !== "Staff"}),
    },
  ];

  const columns = useMemo(
    () => [
      {
        header: t("Role Name"),
        key: "name",
        render: (value, item) => (
          <div>
            <div>{value}</div>
            {item.nameAr && <div className="small">{item.nameAr}</div>}
          </div>
        ),
      },
      {
        header: t("Total Users"),
        key: "totalUsersCount",
      },
      {
        header: t("Permissions"),
        key: "permissions",
        render: (value, item) =>
          item.hasFullPermission ? (
            <Badge bg="success">{t("Full Permission")}</Badge>
          ) : (
            <TruncatedText text={formatPermissions(value)} />
          ),
      },
      {
        header: t("Created Date"),
        key: "createdAt",
        render: (value) => (
          <div className="ltr_text">{formatUTCDate(value)}</div>
        ),
      },
    ],
    [t],
  );

  const renderActions = useCallback(
    (item) => (
      <>
        {canEdit && (
          <>
            <Switch
              checked={item.status === 1}
              handleOnChange={() => {
                handleStatusUpdate({
                  _id: item._id,
                  status: item.status,
                  dispatch,
                  action: updateRoleStatus,
                });
              }}
              disabled={isLoading && loadingItemId === item._id}
            />
            <img
              src={edit}
              width={20}
              height={20}
              alt={t("edit")}
              onClick={() => navigate(`/roles/edit-role?id=${item._id}`)}
            />
          </>
        )}
        <Link to={`/roles/role-details?id=${item._id}`}>
          <img src={view} width={20} height={20} alt="view" />
        </Link>
        {canDelete && (
          <img
            src={trash}
            width={20}
            height={20}
            alt={t("delete")}
            onClick={() => {
              dispatch(
                showDeleteModal({
                  title: t("Delete Role"),
                  message: t("Are you sure you want to delete this Role?"),
                  itemId: item._id,
                  actionType: "ROLE",
                }),
              );
            }}
          />
        )}
      </>
    ),
    [canEdit, canDelete, isLoading, loadingItemId],
  );

  if (!canView) return null;

  return (
    <div className="page-content roles_page">
      <h4 className="mb-4">{t("Roles")}</h4>
      <FilterBar
        keyword={keyword}
        setKeyword={(value) => dispatch(setFilter({ key: "keyword", value }))}
        keywordPlaceholder={t("Search by Name")}
        filters={filtersList}
        selectedFilters={{ keyword, status, orderBy, roleType }}
        onFilterUpdate={(key, value) => dispatch(setFilter({ key, value }))}
        dates={dates}
        setDates={(value) => dispatch(setFilter({ key: "dates", value }))}
        onSearch={getAllData}
        onReset={handleReset}
        isBtn={canAdd}
        btnText={t("Add Role")}
        onBtnClick={() => navigate("/roles/add-role")}
      />

      {loading && <TableShimmer rows={10} columns={4} />}

      {!loading && roles?.length === 0 && (
        <NoDataFound title={t("No roles found")} icon={noRoles} />
      )}
      {!loading && roles?.length > 0 && (
        <>
          <DataTable
            columns={columns}
            data={roles}
            renderActions={renderActions}
            t={t}
          />
          <Paginations
            currentPage={currentPage}
            totalPages={totalPages}
            totalCount={totalCount}
            onPageChange={getAllData}
          />
        </>
      )}
    </div>
  );
};

export default Roles;
